import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { publicApi } from "../lib/api";
import { ArrowLeft, Loader2, Phone } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BRAND } from "../lib/api";

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    publicApi.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Update document title for SEO
  useEffect(() => {
    if (post?.title) {
      document.title = `${post.title} — RRR Paint & Construction`;
    }
    return () => { document.title = "RRR Paint & Construction LLC | Painters in Montgomery, AL | Free Same-Day Quotes"; };
  }, [post]);

  return (
    <main data-testid="blog-post-page" className="relative min-h-screen">
      <Header />
      <article className="pt-36 md:pt-44 pb-24 md:pb-40">
        <div className="max-w-[820px] mx-auto px-6 md:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-brand-muted hover:text-white text-xs uppercase tracking-[0.28em] mb-10">
            <ArrowLeft className="h-4 w-4" /> Back to Painting Tips
          </Link>

          {loading && (
            <div className="flex items-center gap-2 text-brand-muted"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
          )}

          {notFound && (
            <div data-testid="post-not-found" className="border border-brand-border bg-brand-surface p-10">
              <h1 className="font-display uppercase text-4xl">Post not found.</h1>
              <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-brand-blue">
                <ArrowLeft className="h-4 w-4" /> Back to all posts
              </Link>
            </div>
          )}

          {post && (
            <>
              <div className="text-[10px] tracking-[0.3em] uppercase text-brand-muted mb-4">
                {formatDate(post.created_at)} · {post.author}
              </div>
              <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mb-8">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="font-serif italic text-xl md:text-2xl text-brand-muted leading-snug mb-10 border-l-2 border-brand-blue pl-5">
                  {post.excerpt}
                </p>
              )}
              {post.cover_image && (
                <img
                  src={post.cover_image}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto max-h-[520px] object-cover mb-10 border border-brand-border"
                />
              )}

              <div className="prose prose-invert prose-lg max-w-none
                              prose-headings:font-display prose-headings:uppercase prose-headings:tracking-tight
                              prose-h2:text-3xl prose-h3:text-2xl
                              prose-a:text-brand-blue prose-a:no-underline hover:prose-a:underline
                              prose-strong:text-white prose-p:text-white/90 prose-p:leading-relaxed
                              prose-blockquote:border-brand-blue prose-blockquote:text-brand-muted
                              prose-li:text-white/90 prose-hr:border-brand-border prose-img:border prose-img:border-brand-border">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
              </div>

              {post.tags?.length > 0 && (
                <div className="mt-10 flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <span key={t} className="px-3 py-1 text-[10px] tracking-[0.28em] uppercase border border-brand-border text-brand-muted">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="mt-16 border border-brand-border bg-brand-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                <div className="flex-1">
                  <h3 className="font-display uppercase text-2xl md:text-3xl leading-none">
                    Ready to start your project?
                  </h3>
                  <p className="text-brand-muted mt-2 text-sm md:text-base">
                    Free same-day quotes across Central Alabama.
                  </p>
                </div>
                <a href={BRAND.phoneHref}
                   className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blueHover text-white px-6 py-4 font-display uppercase tracking-widest transition-colors">
                  <Phone className="h-4 w-4" /> {BRAND.phone}
                </a>
              </div>
            </>
          )}
        </div>
      </article>
      <Footer />
    </main>
  );
}
